// Loading-ring percentage for a static (non-streamed) scene load — covers
// the work AFTER the bytes arrive, not just the download.
//
// Driven only by REAL progress signals — no time estimates, nothing tuned
// to any particular device:
//   download — bytes received (each file)
//   decode   — SOG WASM decode passes, forwarded from the library's own
//              progress bar (sog-reader-worker.js)
//   push     — splats processed by pushSOGData/pushPLYData (Splat.ts)
//   build    — WebGPU (three's addon) only: geometry + SH packing,
//              buildGaussianSplatGeometry (Splat.ts); kinds ending '+build'
// Each file's stages have fixed shares of that file (PLANS below), and each
// file counts in proportion to its byte size. The shares only shape the
// pacing; correctness never depends on them, since every stage reaches its
// share only when its real work is done.
//
// Two display rules keep it smooth without ever running ahead of reality:
//   - Between the coarse ticks of a stage (e.g. one decode pass), progress
//     creeps toward — but never reaches — the next tick, at the pace of the
//     previous ticks *of this same load*.
//   - The shown value glides toward the target instead of snapping, and
//     never goes backwards.

const PLANS = {
  sog: [['download', 0.6], ['decode', 0.3], ['push', 0.1]],
  ply: [['download', 0.7], ['push', 0.3]],
  cply: [['download', 0.7], ['push', 0.3]],
  // .splat is converted while it downloads; 'finish' is the short tail.
  splat: [['download', 0.95], ['finish', 0.05]],
  'sog+build': [['download', 0.5], ['decode', 0.25], ['push', 0.08], ['build', 0.17]],
  'ply+build': [['download', 0.6], ['push', 0.2], ['build', 0.2]],
  'cply+build': [['download', 0.6], ['push', 0.2], ['build', 0.2]],
  'splat+build': [['download', 0.85], ['finish', 0.0], ['build', 0.15]],
  // Pick-layer point cloud: the worker parse is a capped sample (ms).
  pointcloud: [['download', 0.95], ['finish', 0.05]],
};
const CREEP_REACH = 0.9; // creep approaches at most 90% of the gap to the next tick
const GLIDE_MS = 200; // display smoothing time constant

function newFile(kind) {
  const plan = PLANS[kind] || PLANS.splat;
  return {
    plan,
    total: 0,
    done: false,
    stage: 0, // index into plan of the stage currently running
    stageStart: null,
    value: 0, // last reported fraction of the current stage
    lastAt: null,
    step: 0, // size of the last reported increment
    interval: 0, // time the last increment took
  };
}

export function createLoadProgress({ now = () => performance.now() } = {}) {
  const files = new Map();
  let shown = -1;
  let shownAt = null;

  // Moves file f to stage `name` (stages only move forward; earlier ones
  // count as complete). False for an unknown stage or one already passed.
  function enterStage(f, name, t) {
    const idx = f.plan.findIndex(([n]) => n === name);
    if (idx < 0 || idx < f.stage) return false;
    if (idx > f.stage) {
      f.stage = idx;
      f.stageStart = t;
      f.value = 0;
      f.lastAt = t;
      f.step = 0;
      f.interval = 0;
    }
    return true;
  }

  function report(f, fraction, t) {
    const v = Math.min(1, Math.max(0, fraction));
    if (v <= f.value) return;
    f.step = v - f.value;
    f.interval = t - (f.lastAt ?? f.stageStart ?? t);
    f.value = v;
    f.lastAt = t;
    // A finished stage hands over to the next one immediately.
    if (v >= 1 && f.stage < f.plan.length - 1) {
      f.stage += 1;
      f.stageStart = t;
      f.value = 0;
      f.lastAt = t;
      f.step = 0;
      f.interval = 0;
    }
  }

  function fileFraction(f, t) {
    if (f.done) return 1;
    let sum = 0;
    for (let i = 0; i < f.stage; i++) sum += f.plan[i][1];
    let v = f.value;
    if (f.step > 0 && f.interval > 0 && v < 1) {
      const since = t - f.lastAt;
      // Hyperbolic, not exponential: halfway at one previous-interval,
      // still moving (ever slower) on a pass that runs far longer than the
      // last one, never reaching the next real tick.
      v = Math.min(1, v + f.step * CREEP_REACH * (since / (since + f.interval)));
    }
    return sum + f.plan[f.stage][1] * v;
  }

  return {
    addFile(id, kind) {
      files.set(id, newFile(kind));
    },
    // A retry (e.g. sog → splat fallback) restarts that file from scratch.
    resetFile(id, kind) {
      files.set(id, newFile(kind));
    },
    download(id, loaded, total) {
      const f = files.get(id);
      if (!f || f.done || !(total > 0)) return;
      const t = now();
      f.total = total;
      if (f.stage === 0) report(f, loaded / total, t);
    },
    stage(id, name, fraction) {
      const f = files.get(id);
      if (!f || f.done) return;
      const t = now();
      if (enterStage(f, name, t)) report(f, fraction, t);
    },
    done(id) {
      const f = files.get(id);
      if (f) f.done = true;
    },
    // Integer 0-99, or null until every file's size is known.
    percent() {
      if (files.size === 0) return null;
      const t = now();
      let weighted = 0;
      let weight = 0;
      for (const f of files.values()) {
        if (!(f.total > 0)) {
          if (f.done) continue; // e.g. point cloud failed before a size arrived
          return null;
        }
        weighted += f.total * fileFraction(f, t);
        weight += f.total;
      }
      if (weight <= 0) return null;
      const target = Math.min(99, (weighted / weight) * 100);
      if (shown < 0) {
        shown = target;
      } else if (target > shown) {
        const dt = shownAt == null ? 0 : t - shownAt;
        shown += (target - shown) * (1 - Math.exp(-dt / GLIDE_MS));
      }
      shownAt = t;
      return Math.floor(shown);
    },
  };
}
